import { createRouter, createWebHistory } from 'vue-router'

import { useAuthStore } from '../stores/authStore.js'
import { useScopeStore } from '../stores/scopeStore.js'

import permissionUtility from '../utils/permission.utility.js'
import { ACTION, RESOURCE } from '../config/permission.config.js'
import ticketScopeQueryUtility from '../utils/ticketScopeQuery.utility.js'
import ticketGroupScopeUtility from '../utils/ticketGroupScope.utility.js'
import orgGroupScopeUtility from '../utils/orgGroupScope.utility.js'
import managementListTabUtility from '../utils/managementListTab.utility.js'
import managementViewModeUtility from '../utils/managementViewMode.utility.js'
import ticketActorTabUtility from '../utils/ticketActorTab.utility.js'
import trackingViewTabUtility from '../utils/trackingViewTab.utility.js'
import { goToV1Overview, isV2OverviewPath } from '../utils/v1Navigation.utility.js'

import { initUserSession } from '../features/useUser.js'
import { useScope } from '../features/useScope.js'

import {
  USER_ROLE,
  VIEW_MODE,
  isSuperAdmin,
  resolvePathShell,
  resolveSessionShell,
  shellHome,
} from '../config/auth.config.js'
import { ROUTE_PATHS } from '../config/route.config.js'

import routes from './routes.js'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

function hasScopeTicketGroups(route) {
  return route.matched.some((record) => record.meta?.scopeTicketGroups)
}

function isTicketWorkflowPath(path) {
  return typeof path === 'string' && (path.includes('/tehtavat/hallinta') || path.includes('/tehtavat/seuranta'))
}

function mergeTicketWorkflowQuery(from, to) {
  const resetFilters = ticketScopeQueryUtility.isManagementTrackingNavigation(from.path, to.path)
  let query = resetFilters ? ticketScopeQueryUtility.stripFilters({ ...to.query }) : { ...to.query }

  if (
    hasScopeTicketGroups(from) &&
    hasScopeTicketGroups(to) &&
    from.query.ticketGroup &&
    !query.ticketGroup
  ) {
    query = ticketGroupScopeUtility.withQuery(query, ticketGroupScopeUtility.parseIds(from.query))
  }

  if (
    hasScopeTicketGroups(from) &&
    hasScopeTicketGroups(to) &&
    from.query.orgGroup &&
    !query.orgGroup
  ) {
    query = orgGroupScopeUtility.withQuery(query, orgGroupScopeUtility.parseId(from.query))
  }

  // Tickets vs tasks data mode — shared by hallinta and seuranta (not page-specific).
  if (isTicketWorkflowPath(to.path)) {
    delete query.ticket
    delete query.task
    const dataTab = isTicketWorkflowPath(from.path)
      ? managementListTabUtility.read(from.query)
      : managementListTabUtility.TAB_TICKETS
    query = managementListTabUtility.withTab(query, dataTab)

    delete query.actor
    const actorTab = isTicketWorkflowPath(from.path)
      ? ticketActorTabUtility.read(from.query)
      : ticketActorTabUtility.TAB_ALL
    query = ticketActorTabUtility.withTab(query, actorTab)
  }

  // Statistics vs events — tracking page only.
  if (ticketScopeQueryUtility.isTicketTrackingPath(to.path)) {
    delete query.stats
    delete query.events
    const viewTab = ticketScopeQueryUtility.isTicketTrackingPath(from.path)
      ? trackingViewTabUtility.read(from.query)
      : trackingViewTabUtility.TAB_STATS
    query = trackingViewTabUtility.withTab(query, viewTab)
  } else if (ticketScopeQueryUtility.isTicketManagementListPath(to.path)) {
    delete query.stats
    delete query.events
  }

  // List / week / month / kanban — management list only (`?layout=`).
  if (ticketScopeQueryUtility.isTicketManagementListPath(to.path)) {
    delete query.layout
    const viewMode = ticketScopeQueryUtility.isTicketManagementListPath(from.path)
      ? managementViewModeUtility.read(from.query)
      : managementViewModeUtility.DEFAULT
    query = managementViewModeUtility.withTab(query, viewMode)
  } else {
    delete query.layout
  }

  return ticketScopeQueryUtility.queryEqual(query, to.query)
    ? null
    : { path: to.path, query, hash: to.hash }
}

/** Cancel the SPA navigation and hard-send USER/GROUP shells to v1 overview. */
function leaveToV1Overview(shell) {
  if (shell === VIEW_MODE.ADMIN) return shellHome(shell)
  if (goToV1Overview({ group: shell === VIEW_MODE.GROUP })) return false
  return shellHome(shell)
}

/**
 * Global navigation guard. Awaits the shared boot `init()` promise so hard
 * refreshes and deep links always resolve the session before redirecting.
 * `initPromise` dedupes the call with `main.js` — no extra network round-trip.
 */
router.beforeEach(async (to, from) => {
  await initUserSession(router)

  if (from.name && from.path !== to.path && isTicketWorkflowPath(from.path) && isTicketWorkflowPath(to.path)) {
    const merged = mergeTicketWorkflowQuery(from, to)
    if (merged) return merged
  }

  const authStore = useAuthStore()
  const user = authStore.user

  if (to.meta.requiresAuth && !user) {
    return { path: ROUTE_PATHS.login, query: { redirect: to.fullPath } }
  }

  if (to.meta.requiresGuest && user) {
    const redirect = typeof to.query.redirect === 'string' ? to.query.redirect : ''
    if (redirect.startsWith('/') && !isV2OverviewPath(redirect)) {
      return redirect
    }
    const scopeStore = useScopeStore()
    return leaveToV1Overview(resolveSessionShell(user, scopeStore.scope.kind))
  }

  if (to.matched.some((record) => record.meta.requiresAdmin) && user?.role !== USER_ROLE.SUPER_ADMIN) {
    return leaveToV1Overview(VIEW_MODE.USER)
  }

  if (to.matched.some((record) => record.meta.requiresSuperAdmin) && !isSuperAdmin(user)) {
    return leaveToV1Overview(VIEW_MODE.USER)
  }

  if (to.matched.some((record) => record.meta.requiresGroupMode) && !user?.groupMode) {
    return leaveToV1Overview(VIEW_MODE.USER)
  }

  if (user && to.meta.requiresAuth && !to.meta.scopeNeutral) {
    const scopeStore = useScopeStore()
    const sessionShell = resolveSessionShell(user, scopeStore.scope.kind)
    const targetShell = resolvePathShell(to.path, to.meta)
    if (targetShell && targetShell !== sessionShell) {
      // SuperAdmin → /admin: fix scope instead of bouncing to v1 (refresh / group switch).
      if (targetShell === VIEW_MODE.ADMIN && isSuperAdmin(user)) {
        useScope().setAdmin()
      } else {
        return leaveToV1Overview(sessionShell)
      }
    }
  }

  if (user) {
    const denied = to.matched.find(
      (record) =>
        record.meta.permission &&
        !record.meta.layoutRoot &&
        !permissionUtility.route.enabled(user.userPermissions, record.meta.permission),
    )
    if (denied) {
      if (to.matched.some((record) => record.meta.requiresAdmin)) return `${ROUTE_PATHS.admin}/overview`
      const groupShell = to.matched.some((record) => record.meta.requiresGroupMode)
      return leaveToV1Overview(groupShell ? VIEW_MODE.GROUP : VIEW_MODE.USER)
    }

    const requiresTicketCreate = to.matched.some((record) => record.meta.ticketResourceCreate)
    if (
      requiresTicketCreate &&
      !permissionUtility.resource.enabled(user.userPermissions, RESOURCE.TICKET, ACTION.CREATE)
    ) {
      const managementRoute = to.matched.some((record) => record.meta.requiresGroupMode)
        ? 'GroupTicketManagement'
        : 'TicketManagement'
      return { name: managementRoute, query: to.query }
    }
  }

  // Accidental hit on empty overview stubs → v1 (also covers '' redirects / catch-all).
  if (user && isV2OverviewPath(to.path)) {
    if (goToV1Overview({ group: to.path.startsWith(ROUTE_PATHS.group) })) return false
  }

  if (to.meta.title) {
    document.title = to.meta.title
  }
})

export default router
